import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  type FormControlProps,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  type SelectProps as MuiSelectProps,
} from "@mui/material";
import { Field, type FieldProps, type FieldRenderProps } from "react-final-form";
import { type Except } from "type-fest";

export type ModifiedSelectProps = Omit<
  MuiSelectProps,
  "onBlur" | "onFocus" | "value" | "checked" | "type" | "multiple" | "onChange"
> & { "data-cy"?: string; "data-testid"?: string };

/** Internal item value type */
export interface SelectItem {
  id: string | number;
  value: string;
  type?: string;
}

/** External item value type */
export type JSONSelectItem = `{ "id": ${string | number}, "value": ${string} }`;

/** JSON */
export type SelectValueSingle = JSONSelectItem | undefined;

/** JSON[] */
export type SelectValueMultiple = JSONSelectItem[] | undefined;

/** JSON[] | JSON | undefined */
export type SelectValue = SelectValueSingle | SelectValueMultiple;

export interface SelectProps extends FieldProps<SelectValue, FieldRenderProps<SelectValue>> {
  helperText?: string;
  label?: string;
  required?: boolean;
  selectProps?: ModifiedSelectProps;
  selectItems: SelectItem[];
  formControlProps?: Except<FormControlProps, "error">;
}

/** Parse SelectValueSingle */
export const getParsedSelected = (selected: string) => JSON.parse(selected) as SelectItem;

/** Parse SelectValueMultiple */
export const getParsedSelectedMultiple = (selected: string[]) => selected.map(getParsedSelected);

export const formatSelectValue = (value: SelectItem) => JSON.stringify(value) as JSONSelectItem;

/** @deprecated Prefer ~/ui/atoms/Form/Fields/Select/Select */
export const Select = ({
  required,
  selectItems,
  formControlProps,
  selectProps,
  helperText,
  label,
  ...props
}: SelectProps) => {
  return (
    <Field
      render={({ input, meta: { error } }) => {
        const labelId = `${input.name}-label`;
        return (
          <>
            <FormControl error={Boolean(error)} fullWidth {...formControlProps}>
              {label && (
                <InputLabel id={labelId} sx={{ marginLeft: "-0.875rem" }}>
                  {label}
                </InputLabel>
              )}
              <MuiSelect<string | string[]>
                renderValue={(selected) => {
                  if (input.multiple) {
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        {getParsedSelectedMultiple(selected as string[])?.map((item) => (
                          <Chip
                            key={item.id}
                            label={item.value}
                            size="small"
                            sx={{ display: "flex", flexWrap: "wrap" }}
                          />
                        ))}
                      </Box>
                    );
                  }

                  const parsedItem = getParsedSelected(selected as string);
                  return (
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{parsedItem.value}</span>
                      {parsedItem.type && (
                        <Chip
                          label={parsedItem.type}
                          size="small"
                          color={parsedItem.type === "District" ? "primary" : "secondary"}
                        />
                      )}
                    </Box>
                  );
                }}
                {...selectProps}
                defaultValue={props.defaultValue}
                required={required}
                {...input}
                labelId={labelId}
                value={input.multiple ? ((input.value || []) as string[]) : (input.value as string)}
              >
                {selectItems.map((item, key) => (
                  <MenuItem key={key} value={JSON.stringify(item)}>
                    {input.multiple && (
                      <Checkbox
                        checked={
                          getParsedSelectedMultiple((input.value || []) as string[]).findIndex(
                            ({ id }) => id === item.id,
                          ) > -1
                        }
                      />
                    )}
                    <ListItemText primary={item.value} />
                    {item.type && (
                      <Chip
                        label={item.type}
                        size="small"
                        color={item.type === "District" ? "primary" : "secondary"}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </MuiSelect>
              {(error || helperText) && <FormHelperText>{error || helperText}</FormHelperText>}
            </FormControl>
          </>
        );
      }}
      {...props}
    />
  );
};
